import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import Swal from "sweetalert2";
import jwtDecode from "jwt-decode";
import { CssBaseline } from "@mui/material";
import { useNavigate } from "react-router-dom";
import Home from "../components/campaigns/Home";
import "../components/campaigns/css/Campaigns.css";
import paymentdashboardservice from "../services/paymentdashboardservice";
import DashboardHeaderSection from "../components/common/dashboardheader/DashboardHeader";
import paymentportalservice from "../services/paymentportalservice";
import CreateQueueModal from "../components/paymentqueues/CreateQueueModal";
import GroupSelectionDialog from "../components/campaigns/common/GroupSelectionDialog";
import AddRevenueDialog from "../components/campaigns/common/AddRevenueDialog";
import EditBudgetLimitDialog from "../components/campaigns/common/EditBudgetLimitDialog";
import ShareCampaignDialog from "../components/campaigns/common/ShareCampaignDialog";
import GetCookieByName from "../hooks/GetCookie";

export const CampaignsDashboard = () => {
	useEffect(() => { document.title = "Campaigns | Meesha"; }, []);
	const navigate = useNavigate();
	const [campaigns, setCampaigns] = useState([]);
	const [loading, setLoading] = useState(false);
	const [cursor, setCursor] = useState(null);
	const [hasMore, setHasMore] = useState(true);
	const [totalCampaigns, setTotalCampaigns] = useState();

	const [showCreatorQueue, setShowCreatorQueue] = useState(false);
	const [activeCampaign, setActiveCampaign] = useState(null);

	const [isFiltering, setIsFiltering] = useState(false);
	const [filterOptions, setFilterOptions] = useState({
		categories: [],
		platforms: [],
		creators: [],
		budgetRange: { min: 0, max: 0 },
		limitRange: { min: 0, max: 0 },
	});

	const [groups, setGroups] = useState([]);
	const [viewingGroup, setViewingGroup] = useState(null);
	const [selectionMode, setSelectionMode] = useState(false);
	const [selectedIds, setSelectedIds] = useState(new Set());
	const [groupDialogOpen, setGroupDialogOpen] = useState(false);

	// Decoded once from the auth cookie's JWT — same pattern as
	// components/payments/CategoryCards.jsx — rather than re-decoding in
	// every descendant that needs to gate revenue UI on it.
	const isSuperUser = useMemo(() => {
		try {
			const token = JSON.parse(GetCookieByName("auth"))["token"];
			return Boolean(jwtDecode(token)?.isSuperUser);
		} catch (error) {
			return false;
		}
	}, []);

	const [revenueDialogCampaign, setRevenueDialogCampaign] = useState(null);
	const [budgetLimitDialogCampaign, setBudgetLimitDialogCampaign] = useState(null);
	const [shareDialogCampaign, setShareDialogCampaign] = useState(null);
	const [revenueSummary, setRevenueSummary] = useState(null);
	const [revenueSummaryLoading, setRevenueSummaryLoading] = useState(false);

	const loaderRef = useRef(null);

	const cursorRef = useRef(null);
	const loadingRef = useRef(false);
	const hasMoreRef = useRef(true);
	const isFilteringRef = useRef(false);
	const lastFyRef = useRef('All');
	const searchRequestIdRef = useRef(0);
	// /search is now paginated the same way the plain browse list already is
	// (see routes/campaign.js) — these mirror cursorRef/hasMoreRef/loadingRef
	// above, but for the filtered/sorted list instead of the unfiltered one.
	// lastSearchParamsRef holds whatever (value, filters) handleChange most
	// recently searched with, so "load more" can re-issue the identical query
	// with just the next page number, without the scroll handler needing to
	// know anything about what's currently filtered/sorted.
	const searchPageRef = useRef(1);
	const searchHasMoreRef = useRef(false);
	const searchLoadingMoreRef = useRef(false);
	const lastSearchParamsRef = useRef(null);
	const filterOptionsRequestIdRef = useRef(0);
	const groupsRequestIdRef = useRef(0);
	const revenueSummaryRequestIdRef = useRef(0);
	// Mirrors `revenueSummary` for InitializeCampaigns' pagination guard below
	// (a ref so that guard doesn't need `revenueSummary` in its own dependency
	// list) — kept truthy for as long as a revenue month view is showing, so
	// background browse-mode pages aren't fetched (and silently discarded)
	// while the user is looking at that fixed, unrelated result set.
	const revenueSummaryRef = useRef(null);
	// Bumped every time resetToBrowseMode() restarts pagination from scratch.
	// A browse-mode fetch captures this at the start and re-checks it after
	// its await — needed because by the time an old fetch (kicked off before
	// the reset) resolves, isFilteringRef may already be back to false, so
	// that guard alone can't tell a stale response from a fresh one.
	const paginationGenerationRef = useRef(0);

	const loadFilterOptions = useCallback(async (fy) => {
		const requestId = ++filterOptionsRequestIdRef.current;
		try {
			const options = await paymentportalservice.FetchCampaignFilters(fy);
			// Guards against a slower earlier FY's response landing after a
			// faster later one and overwriting it with stale options.
			if (requestId !== filterOptionsRequestIdRef.current) return;
			setFilterOptions({
				categories: options?.categories || [],
				platforms: options?.platforms || [],
				creators: options?.creators || [],
				budgetRange: options?.budgetRange || { min: 0, max: 0 },
				limitRange: options?.limitRange || { min: 0, max: 0 },
				netRevenueCounts: options?.netRevenueCounts || null,
			});
		} catch (error) {
			console.log("Error loading campaign filter options", error);
		}
	}, []);

	useEffect(() => {
		loadFilterOptions('All');
	}, [loadFilterOptions]);

	const loadGroups = useCallback(async (fy) => {
		const requestId = ++groupsRequestIdRef.current;
		try {
			const data = await paymentportalservice.FetchCampaignGroups(fy);
			if (requestId !== groupsRequestIdRef.current) return;
			setGroups(data || []);
		} catch (error) {
			console.log("Error loading campaign groups", error);
		}
	}, []);

	useEffect(() => {
		loadGroups('All');
	}, [loadGroups]);

	const handleLinkGenerate = useCallback((campaign, isDetail) => {
		if (Number(campaign.paymentLimit) === 0 && isDetail === false) {
			Swal.fire({
				position: "center",
				icon: "error",
				title: "Payment Limit Exceeded",
				showConfirmButton: false,
				timer: 2000,
			});
			return;
		}

		navigate(
			isDetail ? `/payments/campaigns/details/${campaign._id}` : "/payments/paymentlink",
			{
				state: {
					id: campaign._id,
					name: campaign.name,
					category: campaign?.category[0]?.name,
					deliverables: campaign.deliverables,
					platform: campaign.platform[0].name,
					totalPayment: campaign.totalPayment,
					paymentLimit: campaign.paymentLimit,
					createdby: campaign.createdby || campaign.createdby.fullName,
					isOfficeExpense: campaign.isOfficeExpense
				},
			}
		);
	}, [navigate]);

	const editCampaign = async (id) => {
		const { value: totalPayment } = await Swal.fire({
			title: "Increase the Budget",
			input: "text",
			confirmButtonText: "Add",
			showCancelButton: true,
			inputPlaceholder: "Amount",
			inputValidator: (value) => {
				if (!value) {
					return "You need to write something!";
				}
			},
		});

		if (totalPayment) {
			await paymentdashboardservice.updateCampaignBudget({
				id: id,
				totalPayment: totalPayment,
			});
			await Swal.fire({
				position: "center",
				icon: "success",
				title: "Budget Updated Successfully!",
				showConfirmButton: false,
				timer: 1500,
			});
			window.location.reload();
		}
	};

	const handleAddRevenue = useCallback((campaign) => {
		setRevenueDialogCampaign(campaign);
	}, []);

	const closeRevenueDialog = useCallback(() => {
		setRevenueDialogCampaign(null);
	}, []);

	const handleEditBudgetLimit = useCallback((campaign) => {
		setBudgetLimitDialogCampaign(campaign);
	}, []);

	const handleShare = useCallback((campaign) => {
		setShareDialogCampaign(campaign);
	}, []);

	const closeShareDialog = useCallback(() => {
		setShareDialogCampaign(null);
	}, []);

	const closeBudgetLimitDialog = useCallback(() => {
		setBudgetLimitDialogCampaign(null);
	}, []);

	// Same in-place patch as handleRevenueSaved below, so an edit doesn't
	// require a full reload (see editCampaign above for the older pattern
	// this avoids).
	const handleBudgetLimitSaved = useCallback((updatedCampaign) => {
		const updatedId = updatedCampaign._id || updatedCampaign.id;
		setCampaigns((prev) =>
			prev.map((c) => (c._id === updatedId ? { ...c, ...updatedCampaign } : c))
		);
	}, []);

	// No dedicated dialog (unlike Add Revenue / Edit Budget-Limit above) — a
	// freeze/unfreeze is a single confirmed toggle, not a form, so a Swal
	// confirm is all it needs before patching the campaign in place.
	const handleFreezeToggle = useCallback(async (campaign) => {
		const campaignId = campaign._id || campaign.id;
		const freezing = !campaign.isFrozen;

		const confirm = await Swal.fire({
			icon: "warning",
			title: freezing ? "Freeze this campaign?" : "Unfreeze this campaign?",
			text: freezing
				? "No one will be able to edit it until a super user unfreezes it."
				: "Editing will be allowed again for everyone.",
			showCancelButton: true,
			confirmButtonText: freezing ? "Freeze" : "Unfreeze",
		});
		if (!confirm.isConfirmed) return;

		try {
			const updated = await paymentportalservice.SetCampaignFrozen({ campaignId, freeze: freezing });
			setCampaigns((prev) =>
				prev.map((c) => (c._id === campaignId ? { ...c, ...updated } : c))
			);
			Swal.fire({
				icon: "success",
				title: freezing ? "Campaign frozen" : "Campaign unfrozen",
				showConfirmButton: false,
				timer: 1200,
			});
		} catch (error) {
			Swal.fire({
				icon: "error",
				title: "Could not update freeze status",
				text: error?.response?.data || error.message,
			});
		}
	}, []);

	const loadRevenueSummary = useCallback(async (month, year) => {
		// Guards against two races: applying February right after January (the
		// slower of the two requests would otherwise land last and overwrite
		// the correct result), and clearing the view while a fetch is still in
		// flight (that response would otherwise silently revive the view the
		// admin just dismissed) — same pattern as searchRequestIdRef elsewhere
		// in this file.
		const requestId = ++revenueSummaryRequestIdRef.current;
		setRevenueSummaryLoading(true);
		try {
			const summary = await paymentportalservice.FetchRevenueSummary(month, year);
			if (requestId !== revenueSummaryRequestIdRef.current) return;
			setRevenueSummary(summary);
		} catch (error) {
			console.log("Error loading revenue summary", error);
			if (requestId === revenueSummaryRequestIdRef.current) {
				Swal.fire({ icon: "error", title: "Could not load revenue", showConfirmButton: false, timer: 1500 });
			}
		} finally {
			if (requestId === revenueSummaryRequestIdRef.current) {
				setRevenueSummaryLoading(false);
			}
		}
	}, []);

	const clearRevenueSummary = useCallback(() => {
		revenueSummaryRequestIdRef.current += 1;
		setRevenueSummary(null);
	}, []);

	useEffect(() => {
		revenueSummaryRef.current = revenueSummary;
	}, [revenueSummary]);

	// Patches the saved campaign in place instead of reloading the page (see
	// editCampaign below for the older, reload-based pattern this avoids) —
	// and, if a revenue month is currently being viewed, refreshes its total
	// since this save may have just added/changed a campaign within it.
	const handleRevenueSaved = useCallback((updatedCampaign) => {
		const updatedId = updatedCampaign._id || updatedCampaign.id;
		setCampaigns((prev) =>
			prev.map((c) => (c._id === updatedId ? { ...c, ...updatedCampaign } : c))
		);
		if (revenueSummary) {
			loadRevenueSummary(revenueSummary.month, revenueSummary.year);
		}
	}, [revenueSummary, loadRevenueSummary]);

	const handleDelete = async (id, campaignName) => {
		const swalWithBootstrapButtons = Swal.mixin({
			customClass: {
				confirmButton: "btn btn-danger me-2",
				cancelButton: "btn btn-success me-2",
			},
			buttonsStyling: true,
		});

		swalWithBootstrapButtons
			.fire({
				title: "Are you sure?",
				text: "You won't be able to revert this!",
				icon: "warning",
				showCancelButton: true,
				confirmButtonText: "Yes, delete it!",
				cancelButtonText: "No, cancel!",
				reverseButtons: true,
			})
			.then(async (result) => {
				if (result.isConfirmed) {
					let response = await paymentdashboardservice.deleteCampaign({
						campaignId: id,
						campaignName: campaignName,
					});

					if (response.status === 201) {
						Swal.fire({
							icon: "error",
							title: response.data,
							showConfirmButton: false,
							timer: 3000,
						});
						return;
					}

					setCampaigns(prev =>
						prev.filter(campaign => campaign._id !== id)
					);

					swalWithBootstrapButtons.fire(
						"Deleted!",
						"Your file has been deleted.",
						"success"
					);
				}
			});
	};

	const InitializeCampaigns = useCallback(async () => {
		// Also bails while filtering/viewing a group: without this, a
		// background page-load kicked off just before switching away can
		// still be "not loading" (loadingRef resets below) by the time the
		// IntersectionObserver's sentinel becomes visible again under the
		// shorter filtered/group view, triggering another wasted browse-mode
		// fetch that sets the shared `loading` flag true and then — because
		// it gets discarded below — never sets it back false, leaving the
		// filtered/group view's spinner stuck on indefinitely.
		// Also bails while a revenue-month view is active: Home swaps the grid
		// over to that fixed result set, so continuing to fetch and append
		// browse-mode pages in the background would just be wasted requests
		// with nothing to show for them until the view is cleared.
		if (loadingRef.current || !hasMoreRef.current || isFilteringRef.current || revenueSummaryRef.current) {
			return;
		}

		const generation = paginationGenerationRef.current;
		loadingRef.current = true;
		setLoading(true);

		const currentCursor = cursorRef.current;

		const campaignResponse = await paymentportalservice.FetchPendingCampaigns(currentCursor);

		if (generation !== paginationGenerationRef.current) {
			// resetToBrowseMode() restarted pagination from scratch while this
			// fetch was in flight and has already kicked off its own fresh
			// call — applying this stale response (or touching loadingRef,
			// which the fresh call now owns) would corrupt the cursor/hasMore
			// state or silently truncate the list to whatever page this old
			// request happened to be on.
			return;
		}

		loadingRef.current = false;

		if (isFilteringRef.current) {
			// The user switched into filtered/search mode while this page was
			// in flight — these results belong to the old browse-mode list.
			return;
		}

		if (campaignResponse['success'] === true) {
			const { totalCampaigns: dbCampaigns, campaigns: pendingCampaigns, nextCursor } = campaignResponse;
			setCursor(nextCursor);
			cursorRef.current = nextCursor;

			if (!nextCursor) {
				setHasMore(false);
				hasMoreRef.current = false;
			}

			setTotalCampaigns(dbCampaigns);
			setCampaigns(prev => [...prev, ...pendingCampaigns]);
		}

		setLoading(false);
		loadingRef.current = false;
	}, []);

	// Drops back to the plain paginated/browse list once every search term and
	// filter has been cleared — otherwise the last filtered result set would
	// stay on screen forever.
	const resetToBrowseMode = useCallback(() => {
		// Invalidates any search or viewGroup fetch still in flight — without
		// this, exiting a group view a moment before its fetch resolves would
		// let that late response land afterwards and silently overwrite the
		// fresh browse-mode list with stale group data. Also invalidates any
		// search "load more" in flight, for the same reason.
		searchRequestIdRef.current += 1;
		paginationGenerationRef.current += 1;
		searchPageRef.current = 1;
		searchHasMoreRef.current = false;
		lastSearchParamsRef.current = null;
		// A browse-mode fetch that was already in flight when filtering
		// started would otherwise leave loadingRef stuck true and silently
		// block the fresh fetch below — its own response is now moot since
		// the generation bump above makes it a no-op when it lands.
		loadingRef.current = false;
		setIsFiltering(false);
		isFilteringRef.current = false;
		setCampaigns([]);
		setCursor(null);
		cursorRef.current = null;
		setHasMore(true);
		hasMoreRef.current = true;
		InitializeCampaigns();
	}, [InitializeCampaigns]);

	// Single place that actually shapes a /search request, used for both page 1
	// (handleChange, below) and every later page (loadMoreSearchResults) — so
	// the two can never drift on which params get sent for a given filter set.
	const fetchSearchPage = useCallback((value, filters, page) => (
		paymentportalservice.FetchCampaignSearch({
			name: value,
			category: filters.category,
			platform: filters.platform,
			createdby: filters.createdby,
			minBudget: filters.minBudget || undefined,
			maxBudget: filters.maxBudget || undefined,
			minLimit: filters.minLimit || undefined,
			maxLimit: filters.maxLimit || undefined,
			netRevenue: filters.netRevenue && filters.netRevenue !== 'All' ? filters.netRevenue : undefined,
			fy: filters.fy,
			month: filters.month && filters.month !== 'All' ? filters.month : undefined,
			sortBy: filters.sortBy || undefined,
			sortOrder: filters.sortOrder || undefined,
			page,
		})
	), []);

	const handleChange = useCallback(async (value, filters = {}) => {
		if (filters.fy && filters.fy !== lastFyRef.current) {
			lastFyRef.current = filters.fy;
			loadFilterOptions(filters.fy);
			loadGroups(filters.fy);
		}

		const hasActiveFilters = Boolean(
			value?.trim() ||
			filters.category?.length ||
			filters.platform?.length ||
			filters.createdby?.length ||
			filters.minBudget ||
			filters.maxBudget ||
			filters.minLimit ||
			filters.maxLimit ||
			(filters.netRevenue && filters.netRevenue !== 'All') ||
			(filters.fy && filters.fy !== 'All') ||
			(filters.month && filters.month !== 'All') ||
			// A sort choice alone (no other filter active) still has to route
			// through /search rather than the plain cursor-paginated browse
			// list below — sorting only whatever page has loaded so far would
			// silently misorder anything not yet scrolled into view.
			filters.sortBy
		);

		if (hasActiveFilters) {
			// Guards against out-of-order responses: if the user changes a
			// filter again before this request resolves, only the response
			// matching the latest request is ever applied to the list. Also
			// what loadMoreSearchResults checks before appending a later page,
			// so a stale "load more" from a since-superseded search can't land.
			const requestId = ++searchRequestIdRef.current;
			lastSearchParamsRef.current = { value, filters };
			searchPageRef.current = 1;
			searchHasMoreRef.current = false;
			setIsFiltering(true);
			isFilteringRef.current = true;
			setLoading(true);
			try {
				const results = await fetchSearchPage(value, filters, 1);
				if (requestId !== searchRequestIdRef.current) {
					return;
				}
				setCampaigns(results?.campaigns || []);
				setTotalCampaigns(results?.total ?? (results?.campaigns || []).length);
				searchHasMoreRef.current = Boolean(results?.hasMore);
			} catch (error) {
				// Without this, a failed search left `loading` stuck true
				// forever (permanent spinner/dimmed grid) and isFiltering
				// stuck true (pagination silently disabled for the rest of
				// the session), since nothing else ever clears them.
				console.log("Error searching campaigns", error);
				if (requestId === searchRequestIdRef.current) {
					setCampaigns([]);
					searchHasMoreRef.current = false;
				}
			} finally {
				if (requestId === searchRequestIdRef.current) {
					setLoading(false);
				}
			}
			return;
		}

		if (isFilteringRef.current) {
			resetToBrowseMode();
		}
	}, [loadFilterOptions, loadGroups, resetToBrowseMode, fetchSearchPage]);

	// The filtered-list analog of InitializeCampaigns — appends the next page
	// of the same search instead of replacing it, and deliberately never
	// touches `loading` (which drives Home's dim/blocked "re-filtering"
	// treatment): this is a background scroll-triggered append, same as
	// browse mode's own page loads staying silent.
	const loadMoreSearchResults = useCallback(async () => {
		if (searchLoadingMoreRef.current || !searchHasMoreRef.current || !lastSearchParamsRef.current) {
			return;
		}
		searchLoadingMoreRef.current = true;
		const requestId = searchRequestIdRef.current;
		const nextPage = searchPageRef.current + 1;
		try {
			const { value, filters } = lastSearchParamsRef.current;
			const results = await fetchSearchPage(value, filters, nextPage);
			if (requestId !== searchRequestIdRef.current) {
				// A new search superseded this one while the request was in
				// flight — that search's own page 1 already owns the list now.
				return;
			}
			searchPageRef.current = nextPage;
			setCampaigns((prev) => [...prev, ...(results?.campaigns || [])]);
			searchHasMoreRef.current = Boolean(results?.hasMore);
		} catch (error) {
			console.log("Error loading more search results", error);
		} finally {
			searchLoadingMoreRef.current = false;
		}
	}, [fetchSearchPage]);

	// Switches the page into "showing one group's campaigns" mode. Reuses
	// the isFiltering plumbing (dim/spinner/empty-state) since a group view
	// is, mechanically, just another non-paginated result set.
	const viewGroup = useCallback(async (groupId) => {
		// Shares the same request-sequence guard as handleChange's search —
		// clicking two group chips in a row is the identical race.
		const requestId = ++searchRequestIdRef.current;
		setIsFiltering(true);
		isFilteringRef.current = true;
		setLoading(true);
		try {
			const group = await paymentportalservice.FetchCampaignGroupById(groupId, lastFyRef.current);
			if (requestId !== searchRequestIdRef.current) return;
			setViewingGroup(group);
			setCampaigns((group?.campaigns || []).filter((c) => !c.isDeleted));
		} catch (error) {
			// FetchCampaignGroupById rethrows on failure. Without resetting
			// isFiltering here, a failed fetch (e.g. a network blip) left the
			// page permanently in "filtering" mode with no group ever set,
			// silently disabling infinite-scroll pagination for the rest of
			// the session.
			console.log("Error loading campaign group", error);
			if (requestId === searchRequestIdRef.current) {
				setIsFiltering(false);
				isFilteringRef.current = false;
			}
		} finally {
			if (requestId === searchRequestIdRef.current) setLoading(false);
		}
	}, []);

	const exitGroupView = useCallback(() => {
		setViewingGroup(null);
		resetToBrowseMode();
	}, [resetToBrowseMode]);

	// Detaches one campaign from the group being viewed — the campaign
	// itself is untouched, it just stops being tagged with this group.
	const removeCampaignFromGroup = useCallback(async (campaignId) => {
		if (!viewingGroup) return;

		const result = await Swal.fire({
			title: "Remove from group?",
			text: "The campaign itself won't be deleted — it'll just leave this group.",
			icon: "warning",
			showCancelButton: true,
			confirmButtonText: "Remove",
			cancelButtonText: "Cancel",
		});
		if (!result.isConfirmed) return;

		try {
			const updated = await paymentportalservice.RemoveCampaignFromGroup(viewingGroup._id, campaignId);
			setGroups((prev) => prev.map((g) => (g._id === updated._id ? updated : g)));
			setViewingGroup(updated);
			setCampaigns((updated?.campaigns || []).filter((c) => !c.isDeleted));
			Swal.fire({ icon: "success", title: "Removed from group", showConfirmButton: false, timer: 1200 });
		} catch (error) {
			Swal.fire({ icon: "error", title: "Could not remove campaign", showConfirmButton: false, timer: 1500 });
		}
	}, [viewingGroup]);

	const toggleSelectionMode = useCallback(() => {
		setSelectionMode((prev) => {
			if (prev) setSelectedIds(new Set());
			return !prev;
		});
	}, []);

	const toggleSelectCampaign = useCallback((campaignId) => {
		setSelectedIds((prev) => {
			const next = new Set(prev);
			if (next.has(campaignId)) next.delete(campaignId);
			else next.add(campaignId);
			return next;
		});
	}, []);

	const handleCreateGroup = useCallback(async (name) => {
		try {
			const campaignIds = Array.from(selectedIds);
			const group = await paymentportalservice.CreateCampaignGroup({ name, campaignIds });
			setGroups((prev) => [group, ...prev]);
			setSelectedIds(new Set());
			setSelectionMode(false);
			Swal.fire({ icon: "success", title: "Group created", showConfirmButton: false, timer: 1500 });
		} catch (error) {
			Swal.fire({ icon: "error", title: "Could not create group", text: error?.response?.data || error.message });
			// Rethrown so GroupSelectionDialog's `await onCreateNew(...)`
			// rejects — otherwise it always resolved and the dialog closed
			// itself right after showing this error, as if the save had
			// actually succeeded, losing the typed group name.
			throw error;
		}
	}, [selectedIds]);

	const handleAddToExistingGroup = useCallback(async (groupId) => {
		try {
			const campaignIds = Array.from(selectedIds);
			const updated = await paymentportalservice.AddCampaignsToGroup(groupId, campaignIds);
			setGroups((prev) => prev.map((g) => (g._id === updated._id ? updated : g)));
			setSelectedIds(new Set());
			setSelectionMode(false);
			Swal.fire({ icon: "success", title: "Added to group", showConfirmButton: false, timer: 1500 });
		} catch (error) {
			Swal.fire({ icon: "error", title: "Could not add to group", text: error?.response?.data || error.message });
			// See handleCreateGroup: rethrown so the dialog's await rejects
			// instead of closing as if the save had succeeded.
			throw error;
		}
	}, [selectedIds]);

	useEffect(() => {
		const observer = new IntersectionObserver(
			(entries) => {
				if (!entries[0].isIntersecting) return;
				// Same sentinel serves both lists — whichever is actually on
				// screen decides which "load more" it triggers.
				if (isFilteringRef.current) {
					loadMoreSearchResults();
				} else if (!loadingRef.current && hasMoreRef.current) {
					InitializeCampaigns();
				}
			},
			{
				root: null,
				rootMargin: "200px",
				threshold: 0
			}
		)

		if (loaderRef.current) { observer.observe(loaderRef.current) }

		return () => observer.disconnect();
	}, [InitializeCampaigns, loadMoreSearchResults])

	useEffect(() => {
		cursorRef.current = cursor;
	}, [cursor]);

	useEffect(() => {
		loadingRef.current = loading;
	}, [loading]);

	useEffect(() => {
		hasMoreRef.current = hasMore;
	}, [hasMore]);

	const handleCreatorLinkGenerate = useCallback((campaign) => {
		setActiveCampaign(campaign);
		setShowCreatorQueue(true);
	}, []);

	const handleQueueModalDismiss = useCallback(() => {
		setActiveCampaign(null);
		setShowCreatorQueue(false);
	}, []);

	const handleQueueSuccess = useCallback(() => {
		if (!activeCampaign) { return; }

		handleLinkGenerate(activeCampaign, true);
	}, [activeCampaign, handleLinkGenerate]);

	const openGroupDialog = useCallback(() => setGroupDialogOpen(true), []);
	const closeGroupDialog = useCallback(() => setGroupDialogOpen(false), []);

	return (
		<>
			<CssBaseline />
			<DashboardHeaderSection hideSearch />
			<Home
				loading={loading}
				campaigns={campaigns}
				handleLinkGenerate={handleCreatorLinkGenerate}
				handleChange={handleChange}
				editCampaign={editCampaign}
				handleDelete={handleDelete}
				totalCampaigns={totalCampaigns}
				isFiltering={isFiltering}
				filterOptions={filterOptions}
				groups={groups}
				viewingGroup={viewingGroup}
				onSelectGroup={viewGroup}
				onExitGroupView={exitGroupView}
				onRemoveFromGroup={removeCampaignFromGroup}
				selectionMode={selectionMode}
				onToggleSelectionMode={toggleSelectionMode}
				selectedIds={selectedIds}
				onToggleSelect={toggleSelectCampaign}
				onOpenGroupDialog={openGroupDialog}
				isSuperUser={isSuperUser}
				onAddRevenue={handleAddRevenue}
				onEditBudgetLimit={handleEditBudgetLimit}
				onFreezeToggle={handleFreezeToggle}
				onShare={handleShare}
				revenueSummary={revenueSummary}
				revenueSummaryLoading={revenueSummaryLoading}
				onLoadRevenueSummary={loadRevenueSummary}
				onClearRevenueSummary={clearRevenueSummary}
			/>

			<div ref={loaderRef}></div>

			<CreateQueueModal
				open={showCreatorQueue}
				campaignId={activeCampaign?._id || activeCampaign?.id}
				onClose={handleQueueModalDismiss}
				onSuccess={handleQueueSuccess}
			/>

			<GroupSelectionDialog
				open={groupDialogOpen}
				onClose={closeGroupDialog}
				selectedCount={selectedIds.size}
				existingGroups={groups}
				onCreateNew={handleCreateGroup}
				onAddToExisting={handleAddToExistingGroup}
			/>

			<AddRevenueDialog
				open={Boolean(revenueDialogCampaign)}
				campaign={revenueDialogCampaign}
				onClose={closeRevenueDialog}
				onSaved={handleRevenueSaved}
			/>

			<EditBudgetLimitDialog
				open={Boolean(budgetLimitDialogCampaign)}
				campaign={budgetLimitDialogCampaign}
				onClose={closeBudgetLimitDialog}
				onSaved={handleBudgetLimitSaved}
			/>

			{/* No onSaved patch-in-place (unlike the two dialogs above):
			    sharing changes who *other* users can see, which isn't
			    reflected in any field of the campaign rendered here. */}
			{/* Keyed by campaign id so switching from one campaign's dialog straight to
			    another's (without a close/reopen in between) remounts fresh rather than
			    reusing the same instance — otherwise there's a render where the title has
			    already switched to the new campaign but users/selectedIds/loading are still
			    whatever the previous campaign's fetch last left them as, briefly leaving an
			    enabled Save button that would post the old campaign's selection onto the
			    new one's id. */}
			<ShareCampaignDialog
				key={shareDialogCampaign?._id || shareDialogCampaign?.id || "none"}
				open={Boolean(shareDialogCampaign)}
				campaign={shareDialogCampaign}
				onClose={closeShareDialog}
			/>
		</>
	);
};

export default CampaignsDashboard;
